import React from 'react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

// Custom tooltip styles
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-lg p-3">
        <p className="font-medium text-foreground mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value.toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Pie chart tooltip
const PieTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-lg p-3">
        <p className="font-medium text-foreground">{payload[0].name}</p>
        <p className="text-sm text-muted-foreground">{payload[0].value}%</p>
      </div>
    );
  }
  return null;
};

// Legend formatter
const LegendFormatter = (value) => <span className="text-sm text-muted-foreground">{value}</span>;
const LegendFormatterXs = (value) => <span className="text-xs text-muted-foreground">{value}</span>;

// Earn vs Redeem Area Chart
export function EarnRedeemChart({ data, height = 300 }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Points Earned vs Redeemed</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="earnGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(152, 69%, 40%)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="hsl(152, 69%, 40%)" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="redeemGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(4, 85%, 55%)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="hsl(4, 85%, 55%)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(220, 14%, 90%)" vertical={false} />
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: 'hsl(220, 10%, 46%)' }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: 'hsl(220, 10%, 46%)' }}
              tickFormatter={(value) => value >= 1000 ? `${value / 1000}k` : value}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ paddingTop: 20 }}
              formatter={LegendFormatter}
            />
            <Area
              type="monotone"
              dataKey="earned"
              name="Earned"
              stroke="hsl(152, 69%, 40%)"
              strokeWidth={2}
              fill="url(#earnGradient)"
            />
            <Area
              type="monotone"
              dataKey="redeemed"
              name="Redeemed"
              stroke="hsl(4, 85%, 55%)"
              strokeWidth={2}
              fill="url(#redeemGradient)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// Mini Earn/Redeem Chart for wallet
export function MiniEarnRedeemChart({ data, height = 120 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
        <XAxis
          dataKey="month"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 10, fill: 'hsl(220, 10%, 46%)' }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey="earned" name="Earned" fill="hsl(152, 69%, 40%)" radius={[4, 4, 0, 0]} />
        <Bar dataKey="redeemed" name="Redeemed" fill="hsl(4, 85%, 55%)" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// Redemption by Category Pie Chart
export function RedemptionPieChart({ data, height = 300 }) {
  const COLORS = [
    'hsl(4, 85%, 55%)',
    'hsl(152, 69%, 40%)',
    'hsl(210, 100%, 50%)',
    'hsl(38, 92%, 50%)',
    'hsl(174, 72%, 42%)',
  ];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Redemption by Category</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={90}
              paddingAngle={2}
              dataKey="value"
              nameKey="name"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<PieTooltip />} />
            <Legend
              layout="horizontal"
              verticalAlign="bottom"
              align="center"
              formatter={LegendFormatterXs}
            />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// Bar Chart for Admin Trends
export function AdminBarChart({ data, title, height = 300 }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(220, 14%, 90%)" vertical={false} />
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: 'hsl(220, 10%, 46%)' }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: 'hsl(220, 10%, 46%)' }}
              tickFormatter={(value) => value >= 1000 ? `${value / 1000}k` : value}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ paddingTop: 20 }}
              formatter={LegendFormatter}
            />
            <Bar dataKey="earned" name="Earned" fill="hsl(152, 69%, 40%)" radius={[4, 4, 0, 0]} />
            <Bar dataKey="redeemed" name="Redeemed" fill="hsl(4, 85%, 55%)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
